import { useEffect, useMemo, useState } from "react";

import { AlertCircle, AlertTriangle, Check, ChevronsUpDown, Plus, Trash2 } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import { Input } from "~/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useOilTypes, useRawMaterials } from "~/hooks/purchasing";
import { cn } from "~/lib/utils";
import type {
  BatchIngredientOverride,
  RecipeIngredient,
} from "~/types/production";
import { UNIT_LABELS } from "~/types/purchasing";
import { formatCurrency } from "~/utils/helpers";
import { costPerKgClp } from "~/utils/oilConversions";

type StockStatus = "sufficient" | "insufficient";

interface StockInfo {
  currentStock: number;
  requiredQuantity: number;
  status: StockStatus;
  shortage: number;
  unit: string;
}

interface BatchIngredientsEditorProps {
  recipeIngredients: RecipeIngredient[];
  overrides: BatchIngredientOverride[];
  onChange: (overrides: BatchIngredientOverride[]) => void;
  targetQuantity: number;
  onStockStatusChange?: (hasInsufficientStock: boolean) => void;
}

interface IngredientOption {
  id: string;
  name: string;
  code?: string | null;
  unitCost?: number | null;
  unitLabel?: string;
}

interface IngredientSearchComboboxProps {
  availableOils: IngredientOption[];
  availableMaterials: IngredientOption[];
  onSelect: (type: "oil" | "material", id: string) => void;
}

function IngredientSearchCombobox({
  availableOils,
  availableMaterials,
  onSelect,
}: IngredientSearchComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const handleSelect = (type: "oil" | "material", id: string) => {
    onSelect(type, id);
    setOpen(false);
    setSearch("");
  };

  const hasOptions = availableOils.length > 0 || availableMaterials.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[220px] justify-between"
        >
          <span className="flex items-center">
            <Plus className="mr-2 h-4 w-4" />
            Agregar ingrediente
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="end">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar ingrediente..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No se encontraron ingredientes.</CommandEmpty>
            {availableOils.length > 0 && (
              <CommandGroup heading="Aceites">
                {availableOils
                  .filter((oil) =>
                    `${oil.name} ${oil.code ?? ""}`
                      .toLowerCase()
                      .includes(search.toLowerCase())
                  )
                  .map((oil) => (
                    <CommandItem
                      key={oil.id}
                      value={oil.code ? `${oil.code} - ${oil.name}` : oil.name}
                      onSelect={() => handleSelect("oil", oil.id)}
                      className="cursor-pointer"
                    >
                      <Check className="mr-2 h-4 w-4 opacity-0" />
                      <span className="flex-1 truncate">
                        {oil.code ? `${oil.code} - ${oil.name}` : oil.name}
                      </span>
                      {oil.unitCost != null && (
                        <span className="ml-2 shrink-0 text-xs tabular-nums text-muted-foreground">
                          $ {formatCurrency(oil.unitCost)}/{oil.unitLabel}
                        </span>
                      )}
                    </CommandItem>
                  ))}
              </CommandGroup>
            )}
            {availableMaterials.length > 0 && (
              <CommandGroup heading="Materiales">
                {availableMaterials
                  .filter((material) =>
                    `${material.name} ${material.code ?? ""}`
                      .toLowerCase()
                      .includes(search.toLowerCase())
                  )
                  .map((material) => (
                    <CommandItem
                      key={material.id}
                      value={
                        material.code
                          ? `${material.code} - ${material.name}`
                          : material.name
                      }
                      onSelect={() => handleSelect("material", material.id)}
                      className="cursor-pointer"
                    >
                      <Check className="mr-2 h-4 w-4 opacity-0" />
                      <span className="flex-1 truncate">
                        {material.code
                          ? `${material.code} - ${material.name}`
                          : material.name}
                      </span>
                      {material.unitCost != null && (
                        <span className="ml-2 shrink-0 text-xs tabular-nums text-muted-foreground">
                          $ {formatCurrency(material.unitCost)}/
                          {material.unitLabel}
                        </span>
                      )}
                    </CommandItem>
                  ))}
              </CommandGroup>
            )}
            {!hasOptions && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No hay ingredientes disponibles
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function BatchIngredientsEditor({
  recipeIngredients,
  overrides,
  onChange,
  targetQuantity,
  onStockStatusChange,
}: BatchIngredientsEditorProps) {
  const { data: oilTypes = [] } = useOilTypes();
  const { data: rawMaterials = [] } = useRawMaterials();

  // Build the effective ingredients list from recipe + overrides
  const getEffectiveIngredients = () => {
    // Start with recipe ingredients, apply overrides
    const ingredientMap = new Map<string, BatchIngredientOverride>();

    // Add all recipe ingredients
    for (const ri of recipeIngredients) {
      ingredientMap.set(ri.id, {
        recipe_ingredient_id: ri.id,
        raw_material_id: ri.raw_material_id || undefined,
        oil_type_id: ri.oil_type_id || undefined,
        quantity_per_box: ri.quantity_per_box,
        waste_factor: ri.waste_factor,
        notes: ri.notes || undefined,
        is_removed: false,
      });
    }

    // Apply overrides
    for (const override of overrides) {
      if (override.recipe_ingredient_id) {
        // Override existing recipe ingredient
        if (override.is_removed) {
          ingredientMap.delete(override.recipe_ingredient_id);
        } else {
          ingredientMap.set(override.recipe_ingredient_id, override);
        }
      } else {
        // New ingredient (no recipe_ingredient_id)
        const tempId = `new-${override.oil_type_id || override.raw_material_id}`;
        ingredientMap.set(tempId, override);
      }
    }

    return Array.from(ingredientMap.entries());
  };

  const effectiveIngredients = getEffectiveIngredients();

  // Check if an ingredient has been modified from recipe
  const isModified = (
    ingredientId: string | undefined,
    override: BatchIngredientOverride
  ) => {
    if (!ingredientId) return true; // New ingredient
    const original = recipeIngredients.find((ri) => ri.id === ingredientId);
    if (!original) return true;
    return (
      override.quantity_per_box !== original.quantity_per_box ||
      override.waste_factor !== original.waste_factor
    );
  };

  // Handle quantity change
  const handleQuantityChange = (
    recipeIngredientId: string | undefined,
    newQuantity: number,
    currentOverride: BatchIngredientOverride
  ) => {
    const newOverrides = [...overrides];
    const existingIdx = newOverrides.findIndex(
      (o) => o.recipe_ingredient_id === recipeIngredientId
    );

    const updatedOverride = {
      ...currentOverride,
      quantity_per_box: newQuantity,
    };

    if (existingIdx >= 0) {
      newOverrides[existingIdx] = updatedOverride;
    } else {
      newOverrides.push(updatedOverride);
    }

    onChange(newOverrides);
  };

  // Handle remove ingredient
  const handleRemove = (
    recipeIngredientId: string | undefined,
    override: BatchIngredientOverride
  ) => {
    if (recipeIngredientId) {
      // Mark as removed (it's from recipe)
      const newOverrides = overrides.filter(
        (o) => o.recipe_ingredient_id !== recipeIngredientId
      );
      newOverrides.push({
        ...override,
        is_removed: true,
      });
      onChange(newOverrides);
    } else {
      // Remove from overrides (it's a new ingredient)
      onChange(
        overrides.filter(
          (o) =>
            o.oil_type_id !== override.oil_type_id ||
            o.raw_material_id !== override.raw_material_id ||
            o.recipe_ingredient_id !== undefined
        )
      );
    }
  };

  // Handle add new ingredient
  const handleAddIngredient = (type: "oil" | "material", id: string) => {
    // Check if already exists
    const exists = effectiveIngredients.some(([, ing]) =>
      type === "oil" ? ing.oil_type_id === id : ing.raw_material_id === id
    );
    if (exists) return;

    const newIngredient: BatchIngredientOverride = {
      oil_type_id: type === "oil" ? id : undefined,
      raw_material_id: type === "material" ? id : undefined,
      quantity_per_box: 0,
      waste_factor: 1.0,
    };

    onChange([...overrides, newIngredient]);
  };

  // Get ingredient name and unit
  const getIngredientInfo = (override: BatchIngredientOverride) => {
    if (override.oil_type_id) {
      const oil = oilTypes.find((o) => o.id === override.oil_type_id);
      return {
        name: oil?.code ? `${oil.code} - ${oil.name}` : oil?.name || "Aceite",
        unit: "kg",
        type: "oil" as const,
      };
    }
    if (override.raw_material_id) {
      const material = rawMaterials.find(
        (m) => m.id === override.raw_material_id
      );
      return {
        name: material?.code
          ? `${material.code} - ${material.name}`
          : material?.name || "Material",
        unit: material ? UNIT_LABELS[material.unit] || material.unit : "units",
        type: "material" as const,
      };
    }
    return { name: "Desconocido", unit: "units", type: "material" as const };
  };

  // Get current unit cost (WAC) for an ingredient — oils per kg (÷ density),
  // materials per their own unit. Null when no cost is on file.
  const getUnitCostClp = (
    override: BatchIngredientOverride
  ): number | null => {
    if (override.oil_type_id) {
      const oil = oilTypes.find((o) => o.id === override.oil_type_id);
      if (!oil || oil.average_cost_clp == null || !oil.density_kg_per_liter)
        return null;
      return costPerKgClp(oil.average_cost_clp, oil.density_kg_per_liter);
    }
    if (override.raw_material_id) {
      const material = rawMaterials.find(
        (m) => m.id === override.raw_material_id
      );
      return material?.average_cost_clp ?? null;
    }
    return null;
  };

  // Calculate total for this ingredient
  const calculateTotal = (override: BatchIngredientOverride) => {
    // Treat waste_factor of 0 as 1.0 (no waste/100% efficiency)
    const effectiveWasteFactor = override.waste_factor || 1.0;
    return override.quantity_per_box * effectiveWasteFactor * targetQuantity;
  };

  // Get stock info for an ingredient
  const getStockInfo = (override: BatchIngredientOverride): StockInfo | null => {
    const requiredQuantity = calculateTotal(override);

    if (override.oil_type_id) {
      const oil = oilTypes.find((o) => o.id === override.oil_type_id);
      if (!oil) return null;
      // Convert liters to kg using density (recipe quantities are in kg)
      const currentStockKg = oil.current_stock_liters * oil.density_kg_per_liter;
      const shortage = currentStockKg - requiredQuantity;
      return {
        currentStock: currentStockKg,
        requiredQuantity,
        status: shortage < 0 ? "insufficient" : "sufficient",
        shortage,
        unit: "kg",
      };
    }

    if (override.raw_material_id) {
      const material = rawMaterials.find(
        (m) => m.id === override.raw_material_id
      );
      if (!material) return null;
      const currentStock = material.current_stock;
      const shortage = currentStock - requiredQuantity;
      return {
        currentStock,
        requiredQuantity,
        status: shortage < 0 ? "insufficient" : "sufficient",
        shortage,
        unit: UNIT_LABELS[material.unit] || material.unit,
      };
    }

    return null;
  };

  // Calculate ingredients with insufficient stock
  const insufficientIngredients = useMemo(() => {
    return effectiveIngredients.filter(([, override]) => {
      const stockInfo = getStockInfo(override);
      return stockInfo?.status === "insufficient";
    });
  }, [effectiveIngredients, oilTypes, rawMaterials, targetQuantity]);

  // Notify parent about stock status
  useEffect(() => {
    onStockStatusChange?.(insufficientIngredients.length > 0);
  }, [insufficientIngredients.length, onStockStatusChange]);

  // Get available oils/materials not already in the list, carrying unit cost
  const availableOils: IngredientOption[] = oilTypes
    .filter(
      (oil) =>
        !effectiveIngredients.some(([, ing]) => ing.oil_type_id === oil.id)
    )
    .map((oil) => ({
      id: oil.id,
      name: oil.name,
      code: oil.code,
      unitCost:
        oil.average_cost_clp != null && oil.density_kg_per_liter
          ? costPerKgClp(oil.average_cost_clp, oil.density_kg_per_liter)
          : null,
      unitLabel: "kg",
    }));
  const availableMaterials: IngredientOption[] = rawMaterials
    .filter(
      (mat) =>
        !effectiveIngredients.some(([, ing]) => ing.raw_material_id === mat.id)
    )
    .map((mat) => ({
      id: mat.id,
      name: mat.name,
      code: mat.code,
      unitCost: mat.average_cost_clp ?? null,
      unitLabel: UNIT_LABELS[mat.unit] ?? mat.unit,
    }));

  // Estimated total cost of the batch (sum of costed lines only)
  const batchTotalCost = useMemo(() => {
    return effectiveIngredients.reduce((sum, [, override]) => {
      const unitCost = getUnitCostClp(override);
      return unitCost != null ? sum + unitCost * calculateTotal(override) : sum;
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveIngredients, oilTypes, rawMaterials, targetQuantity]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Ingredientes del Lote</CardTitle>
          <IngredientSearchCombobox
            availableOils={availableOils}
            availableMaterials={availableMaterials}
            onSelect={handleAddIngredient}
          />
        </div>
      </CardHeader>
      <CardContent>
        {insufficientIngredients.length > 0 && (
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Stock Insuficiente</AlertTitle>
            <AlertDescription>
              {insufficientIngredients.length} ingrediente(s) no tienen stock
              suficiente para este lote.
            </AlertDescription>
          </Alert>
        )}

        {effectiveIngredients.length === 0 ? (
          <div className="rounded-lg border border-dashed py-8 text-center text-muted-foreground">
            <AlertCircle className="mx-auto mb-2 h-8 w-8 opacity-50" />
            No hay ingredientes. Seleccione una receta o agregue ingredientes
            manualmente.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ingrediente</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="w-[140px] text-right">
                  Cantidad/Caja
                </TableHead>
                <TableHead className="w-[140px] text-right">
                  Total ({targetQuantity} cajas)
                </TableHead>
                <TableHead className="w-[130px] text-right">
                  Valor Unitario
                </TableHead>
                <TableHead className="w-[130px] text-right">
                  Costo Total
                </TableHead>
                <TableHead className="w-[140px] text-right">
                  Stock Disponible
                </TableHead>
                <TableHead className="w-[100px]">Estado</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {effectiveIngredients.map(([key, override]) => {
                const { name, unit, type } = getIngredientInfo(override);
                const modified = isModified(
                  override.recipe_ingredient_id,
                  override
                );
                const total = calculateTotal(override);
                const stockInfo = getStockInfo(override);
                const unitCost = getUnitCostClp(override);
                const lineCost = unitCost != null ? unitCost * total : null;

                return (
                  <TableRow
                    key={key}
                    className={cn(
                      stockInfo?.status === "insufficient" && "bg-red-50/50"
                    )}
                  >
                    <TableCell className="font-medium">{name}</TableCell>
                    <TableCell>
                      <Badge variant={type === "oil" ? "default" : "secondary"}>
                        {type === "oil" ? "Aceite" : "Material"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Input
                          type="number"
                          step="any"
                          min="0"
                          value={override.quantity_per_box || ""}
                          onChange={(e) =>
                            handleQuantityChange(
                              override.recipe_ingredient_id,
                              parseFloat(e.target.value) || 0,
                              override
                            )
                          }
                          className="w-[100px] text-right"
                        />
                        <span className="w-[40px] text-xs text-muted-foreground">
                          {unit}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {total.toFixed(2)} {unit}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {unitCost != null ? (
                        <>
                          $ {formatCurrency(unitCost)}
                          <span className="text-xs text-muted-foreground">
                            /{unit}
                          </span>
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {lineCost != null ? (
                        `$ ${formatCurrency(lineCost)}`
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {stockInfo && (
                        <div className="flex items-center justify-end gap-2">
                          <span
                            className={cn(
                              "font-mono text-sm",
                              stockInfo.status === "insufficient" &&
                                "font-medium text-red-600"
                            )}
                          >
                            {stockInfo.currentStock.toFixed(2)} {stockInfo.unit}
                          </span>
                          {stockInfo.status === "insufficient" && (
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {!override.recipe_ingredient_id ? (
                        <Badge
                          variant="outline"
                          className="border-blue-200 bg-blue-50 text-blue-700"
                        >
                          Nuevo
                        </Badge>
                      ) : modified ? (
                        <Badge
                          variant="outline"
                          className="border-yellow-200 bg-yellow-50 text-yellow-700"
                        >
                          Modificado
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Original</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          handleRemove(override.recipe_ingredient_id, override)
                        }
                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow className="border-t-2 hover:bg-transparent">
                <TableCell
                  colSpan={5}
                  className="text-right font-semibold text-muted-foreground"
                >
                  Costo Total Estimado del Lote
                </TableCell>
                <TableCell className="text-right font-bold tabular-nums">
                  $ {formatCurrency(batchTotalCost)}
                </TableCell>
                <TableCell colSpan={3} />
              </TableRow>
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
